import React from 'react';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { EMPTY_STRING } from '@uplift/constants';
import * as Yup from 'yup';
import { VALIDATION_FIRST_NAME, VALIDATION_LAST_NAME, VALIDATION_HOME_PHONE } from '@uplift/validations';
import { useCreateProviderReferralMutation } from '@uplift/queries';
import { FormModal } from '../../../../Form/FormModal/FormModal.component';
import { ModalContent, ModalActions } from '../../../../Modal';
import { TextInput } from '../../../../Inputs/TextInput';
import { Select } from '../../../../Inputs/Select';
import { StatesAutocomplete } from '../../../../Inputs/StatesAutocomplete/StatesAutocomplete.component';
import { SignOnBonusPrescriberType, SignOnBonusReferralSource, isApiError, CreateProviderReferralResponse } from '@uplift/api';
import { Alert } from '../../../../Alert/alert.components';
import { CancelButton } from '../../../../Form/FormButtons/CancelButton.component';
import { SubmitButton } from '../../../../Form/FormButtons/SubmitButton.component';
import { useAlert } from '../../../../AlertProvider/mui-alerts.component';
import { MaskedInput } from '../../../../Inputs';

export type ReferProviderModalProps = {
  open: boolean;
  onClose: () => void;
  source: SignOnBonusReferralSource;
  providerId: string;
};

type ProviderSignOnBonusReferralFormType = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  prescriberType?: SignOnBonusPrescriberType;
  stateIds: string[];
};

const initialValues: ProviderSignOnBonusReferralFormType = {
  firstName: EMPTY_STRING,
  lastName: EMPTY_STRING,
  email: EMPTY_STRING,
  phone: EMPTY_STRING,
  prescriberType: undefined,
  stateIds: [],
};

const getBonusAmount = (prescriberType?: SignOnBonusPrescriberType) => {
  if (prescriberType === SignOnBonusPrescriberType.NURSE_PRACTITIONER || prescriberType === SignOnBonusPrescriberType.PHYSICIAN_ASSISTANT) return '$250';

  return '$500';
};

const ReferralBonusAlert: React.FC<{ isAdminReferral: boolean }> = ({ isAdminReferral }) => {
  const { values } = useFormikContext<ProviderSignOnBonusReferralFormType>();

  if (!values.prescriberType) return (
    <Alert showIcon={false} severity="success" sx={{ justifyContent: 'center' }}>
      {isAdminReferral
        ? 'This provider will get a bonus when their referral completes their first appointment!'
        : 'You\'ll get a bonus when your referral completes their first appointment!'}
    </Alert>
  );

  const bonusAmount = getBonusAmount(values.prescriberType);

  return (
    <Alert showIcon={false} severity="success" sx={{ justifyContent: 'center' }}>
      {isAdminReferral
        ? `This provider will get ${bonusAmount} when their referral completes their first appointment!`
        : `You'll get ${bonusAmount} when your referral completes their first appointment!`}
    </Alert>
  );
};

const validationSchema = Yup.object({
  firstName: VALIDATION_FIRST_NAME,
  lastName: VALIDATION_LAST_NAME,
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: VALIDATION_HOME_PHONE,
  prescriberType: Yup.string().required('Prescriber type is required'),
  stateIds: Yup.array().of(Yup.string()).min(1, 'At least one state is required'),
});

export const ReferProviderModal: React.FC<ReferProviderModalProps> = ({ open, onClose, source, providerId }) => {
  const isAdminReferral = source === SignOnBonusReferralSource.ADMIN;
  const createReferral = useCreateProviderReferralMutation(providerId);
  const alert = useAlert();

  const onSubmit = async (values: ProviderSignOnBonusReferralFormType, helpers: FormikHelpers<ProviderSignOnBonusReferralFormType>) => {
    try {
      if (!values.prescriberType) throw new Error('Prescriber type is required');

      await createReferral.mutateAsync({
        ...values,
        prescriberType: values.prescriberType,
        phone: values.phone || undefined,
        referralSource: source,
      });
      helpers.resetForm();
      alert.success('Referral submitted successfully');
      onClose();
    } catch (error) {
      if (isApiError<CreateProviderReferralResponse>(error)) {
        if (error.response?.data.error_key === 'email_already_exists') {
          helpers.setFieldError('email', error.response.data.error);
          alert.error(error.response.data.error);
        } else if (error.response?.data.error) {
          alert.error(error.response.data.error);
        } else{
          alert.error('An error occurred while referring a provider');
        }
      } else {
        alert.error('An error occurred while referring a provider');
      }
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <FormModal open={open} onClose={onClose} title="Refer a Provider" maxWidth="md" fullWidth>
        <ModalContent sx={{ gap: 2, paddingTop: '12px' }}>
          <ReferralBonusAlert isAdminReferral={isAdminReferral} />
          <TextInput name="firstName" label="Referral First Name" fullWidth color="secondary" />
          <TextInput name="lastName" label="Referral Last Name" fullWidth color="secondary" />
          <TextInput name="email" label="Referral Email" fullWidth color="secondary" />
          <MaskedInput type="phone" name="phone" label="Referral Phone" fullWidth color="secondary" />
          <Select
            name="prescriberType"
            label={isAdminReferral ? 'License Type' : 'Prescriber Type'}
            fullWidth
            color="secondary"
            MenuProps={{ disablePortal: true }}
            items={[
              { value: SignOnBonusPrescriberType.MEDICAL_DOCTOR, label: 'Medical Doctor' },
              { value: SignOnBonusPrescriberType.DOCTOR_OF_OSTEOPATHY, label: 'Doctor of Osteopathy' },
              { value: SignOnBonusPrescriberType.NURSE_PRACTITIONER, label: 'Nurse Practitioner' },
              { value: SignOnBonusPrescriberType.PHYSICIAN_ASSISTANT, label: 'Physician Assistant' },
            ]}
          />
          <StatesAutocomplete name="stateIds" label="States" fullWidth multiple color="secondary" />
        </ModalContent>
        <ModalActions>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <SubmitButton color="secondary">Submit</SubmitButton>
        </ModalActions>
      </FormModal>
    </Formik>
  );
};
